import React, { useEffect, useState, useCallback } from 'react';
import TrackingMap from './TrackingMap';
import { titleCase, upperCase, formatDateOnly, formatDateTime } from '../../../lib/utils';
import { useBreadcrumbs } from '../../../app/breadcrumb-provider';
import { TrackingDataFTL } from '../../../types/tracking';
import OrderDetails from './OrderDetails';
import ShareLinkModal from 'components/ui/share-link-modal';
import { Button } from 'components/ui/button';
import { generateShareLink } from 'features/shipments/api';

const TRUCK_W = 70; // px
const TRUCK_H = 48; // px
const GAP_PX = 0;

interface CustomProgressBarProps {
    progress: number;
}

const CustomProgressBar: React.FC<CustomProgressBarProps> = ({ progress }) => {
    const pct = Math.max(0, Math.min(progress, 100));
    const truckLeft = `min(${pct}%, calc(100% - ${TRUCK_W / 2}px))`;

    return (
        <div className="relative flex items-center justify-between mb-2 px-1">
            <div className="w-3 h-3 bg-green-500 rounded-full z-10 flex-shrink-0" />

            <div className="flex-1 mx-3 relative flex items-center overflow-visible">
                {pct > 0 && (
                    <div
                        className="h-1 bg-blue-500 absolute left-0 top-1/2 -translate-y-1/2 z-10"
                        style={{ width: `max(0px, calc(${pct}% - ${GAP_PX / 2 + TRUCK_W / 2}px))` }}
                    />
                )}

                <div
                    className="h-0 border-t-2 border-dotted border-gray-300 absolute top-1/2 -translate-y-1/2 z-0"
                    style={{
                        left: `calc(${pct}% + ${GAP_PX / 2 + TRUCK_W / 2}px)`,
                        width: `max(0px, calc(100% - (${pct}% + ${GAP_PX / 2 + TRUCK_W / 2}px)))`,
                        borderTopWidth: '2px',
                    }}
                />

                <div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 pointer-events-none"
                    style={{ left: truckLeft, width: TRUCK_W, height: TRUCK_H }}
                >
                    <img
                        src="/images/truck.png"
                        alt="Truck"
                        className="w-full h-full object-contain select-none"
                        draggable={false}
                    />
                </div>
            </div>
            <div className="w-3 h-3 bg-gray-300 rounded-full z-10 flex-shrink-0" />
        </div>
    );
};

interface TrackingPageFTLProps {
    data: TrackingDataFTL;
    refreshData: () => void;
    isPublic?: boolean;
}

const TrackingPageFTL: React.FC<TrackingPageFTLProps> = ({ data, refreshData, isPublic }) => {
    const { setBreadcrumbs } = useBreadcrumbs();

    const cnNumber = data.cn_number;
    const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
    const [shareUrl, setShareUrl] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    // Derived state
    const progressPct = data ? Math.round((data.progress.covered_distance / data.progress.total_distance) * 100) : 0;

    // Set breadcrumbs
    useEffect(() => {
        setBreadcrumbs([
            { label: 'Shipments', href: '/shipments' },
            { label: `Track ${cnNumber || ''}`, href: '#' }
        ]);
    }, [setBreadcrumbs, cnNumber]);

    const lastUpdatedFormatted = formatDateTime(data.last_updated);

    const handleShareClick = async () => {
        if (!cnNumber) return;
        try {
            const response = await generateShareLink(cnNumber);
            const fullUrl = `${window.location.origin}/shipments/track/public?token=${response.share_token}`;
            setShareUrl(fullUrl);
            setIsShareModalOpen(true);
        } catch (err) {
            setError("Failed to generate share link.");
        }
    };

    const handleCopy = async() => {
        await navigator.clipboard.writeText(shareUrl);
        setIsShareModalOpen(false);
    };

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {!isPublic && (
                <div className="absolute top-4 right-6 z-30">
                    <Button variant="outline" size="sm" onClick={handleShareClick}>
                        <span className="mr-2">🔗</span> Share Link
                    </Button>
                </div>
            )}
            <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-6 px-6 py-6 pb-6 overflow-hidden">
                <div className="lg:w-[50%] w-full flex flex-col min-h-0">
                    {error && (
                        <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">
                            {error}
                        </div>
                    )}
                    {isPublic && (
                        <div className="mb-6 text-center md:text-left">
                            <img src="/logo.png" className="h-10 mb-4 mx-auto md:mx-0" />
                            <h2 className="text-xl font-bold text-gray-800">Shipment Tracking</h2>
                            <p className="text-sm text-gray-500">
                                CN Number: <span className="font-semibold text-gray-700">{data.cn_number}</span>
                            </p>
                        </div>
                    )}
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6 md:gap-6 mb-8">
                        <div className="text-left min-w-0 flex-1">
                            <div className="text-gray-600 text-sm">From</div>
                            <div className="text-lg font-bold text-gray-900 mb-1 break-words">
                                {upperCase(data.consignor)}
                            </div>
                            <div className="text-md text-gray-700 mb-1">
                                left {titleCase(data.origin)}
                            </div>
                            <div className="text-left">
                                <div className="text-sm font-semibold text-gray-900">
                                    {data.left_date ? formatDateOnly(data.left_date) : '-'}
                                </div>
                            </div>
                        </div>
                        <div className="text-left md:text-right min-w-0 flex-1">
                            <div className="text-gray-600 text-sm">To</div>
                            <div className="text-lg font-bold text-gray-900 mb-1 break-words">
                                {upperCase(data.consignee)}
                            </div>
                            <div className="text-md text-gray-700 mb-1">
                                arriving at {titleCase(data.destination)}
                            </div>
                            <div className="text-left md:text-right">
                                <div className="text-sm font-semibold text-gray-900">
                                    {formatDateOnly(data.eta)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-8">
                        <CustomProgressBar progress={progressPct} />
                        <div className="flex justify-between text-sm text-gray-500 mt-2 px-1">
                            <span>{data.progress.covered_distance} km travelled</span>
                            <span>{data.progress.total_distance} km total distance</span>
                            <span>{data.progress.remaining_distance} km remaining</span>
                        </div>
                    </div>

                    {/* Current Status */}
                    <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-blue-900">
                                Currently in {data.current_address}
                            </span>
                        </div>
                        <div className="text-xs text-blue-700 mt-1">
                            Last updated: {lastUpdatedFormatted.date} {lastUpdatedFormatted.time}
                        </div>
                    </div>

                    {/* Order Details */}
                    <div className="mt-6 flex-1 min-h-0">
                        <OrderDetails orderDetails={data.order_details} className="h-full flex flex-col" />
                    </div>
                </div>
                {data.cn_number && (
                    <div className="lg:w-[50%] w-full min-h-0 flex flex-col">
                        <TrackingMap cnNumber={data.cn_number} onDataRefresh={refreshData} isPublic={isPublic} />
                    </div>
                )}
            </div>

            <ShareLinkModal
                isOpen={isShareModalOpen}
                shareUrl={shareUrl}
                onCopy={handleCopy}
                onClose={() => setIsShareModalOpen(false)}
            />
        </div>
    );
};

export default TrackingPageFTL;
